import React from "react";

import useStoreState from "@/components/providers/useStoreState";
import SelectionStore from "@/stores/SelectionStore";
import SiteDesignStore from "@/stores/SiteDesignStore";
import GroupStore from "@/stores/GroupStore";
import IDUtils from "@/utils/IDUtils";
import BlockUtils from "@/utils/BlockUtils";

import styles from "./ImageCropOverlay.module.css";

/**
 * Dark overlay with a cutout representing the group area.
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS} Layouts
 *
 * @param {Object} props
 * @param {Layouts[number]} props.layout
 */
const GroupFocusOverlay = ({ layout }) => {
  const groupPath = useStoreState(GroupStore, () =>
    GroupStore.getGroupSelectionId(),
  );
  const groupDimensions = useStoreState(GroupStore, () =>
    GroupStore.isFocused() ? GroupStore.getElementDimensions(layout) : null,
  );

  const blockData = SelectionStore.getBlock();
  const block = SiteDesignStore.getBlock(blockData.get("id"));

  // return early if no block is selected or the group isn't focused
  if (!block || !groupPath || !groupDimensions) return null;

  const groupPathArray = IDUtils.getArray(groupPath);
  const groupData = block.getIn(groupPathArray.slice(1));

  // don't show overlay if hidden in this layout
  if (!BlockUtils.isVisibleInLayout(groupData, layout)) return null;

  const { x, y, h, w, a } = groupDimensions.toJS();

  const dimensionVars = {
    "--dimensions-x": x,
    "--dimensions-y": y,
    "--dimensions-w": w,
    "--dimensions-h": h,
    "--dimensions-a": a,
  };

  return (
    <div className={styles.shield} id={`crop-overlay-bg-${layout}`} style={dimensionVars}>
      <div className={styles.hole} data-testid={`crop-overlay-hole-${layout}`} />
    </div>
  );
};

export default GroupFocusOverlay;
